var _X;
import clsx from 'clsx';
import * as React from 'react';
import { useRole, useClick, useDismiss, useFloating, useInteractions, FloatingFocusManager, useMergeRefs } from '@floating-ui/react';
import { X } from '@ovpn-ui/icons';
import { colors, breakPoints } from '@ovpn-ui/styles';
import { Text } from '@ovpn-ui/core';
import { use100vh } from '@ovpn-ui/utils';
import Portal from '../_internal/components/Portal';
import * as Styled from './Modal.styles';
import { getMaxWidth } from './Modal.utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const calculateMaxHeight = windowHeight => {
  const baseScreenPadding = 112;
  const mediumScreenPadding = 128;
  const smallScreenPadding = 52;
  let maxHeight = `calc(${windowHeight}px - ${baseScreenPadding}px)`;
  if (windowHeight <= breakPoints.MD && windowHeight >= breakPoints.XS) {
    maxHeight = `calc(${windowHeight}px - ${mediumScreenPadding}px)`;
  } else if (windowHeight < breakPoints.XS) {
    maxHeight = `calc(${windowHeight}px - ${smallScreenPadding}px)`;
  }
  return maxHeight;
};
const Modal = /*#__PURE__*/React.forwardRef(function Modal({
  // content
  title,
  description,
  children,
  // behaviour
  onClose,
  open = false,
  closeOnClickOutside = false,
  // https://floating-ui.com/docs/floatingoverlay#lockscroll
  disableScrollLock = false,
  disableEscapeKeyDown = false,
  initialFocus = 0,
  // appearance
  style,
  persistent = false,
  footer,
  as = 'div',
  size = 'md',
  className,
  // box default props
  maxWidth,
  ...htmlAttributes
}, ref) {
  const windowHeight = parseInt(use100vh(), 10);
  const windowWidth = getMaxWidth(size);
  const boxProps = {
    maxWidth: maxWidth || windowWidth
  };
  const {
    context,
    refs
  } = useFloating({
    open,
    onOpenChange: onClose
  });
  const role = useRole(context);
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    bubbles: false,
    escapeKey: !disableEscapeKeyDown,
    outsidePress: closeOnClickOutside
  });
  const {
    getFloatingProps
  } = useInteractions([click, role, dismiss]);
  const combinedRef = useMergeRefs([ref, refs.setFloating]);
  if (!open) {
    return null;
  }
  return /*#__PURE__*/_jsx(Portal, {
    children: /*#__PURE__*/_jsx(Styled.Overlay, {
      lockScroll: !disableScrollLock,
      children: /*#__PURE__*/_jsx(FloatingFocusManager, {
        context: context,
        initialFocus: initialFocus,
        children: /*#__PURE__*/_jsxs(Styled.Container, {
          forwardedAs: as,
          ref: combinedRef,
          style: {
            ...style,
            maxHeight: calculateMaxHeight(windowHeight)
          },
          size: size,
          "aria-modal": "true",
          "aria-labelledby": "modal_label",
          "aria-describedby": "modal_desc",
          className: clsx('ovpn-modal-container', className),
          ...boxProps,
          ...getFloatingProps(),
          ...htmlAttributes,
          children: [/*#__PURE__*/_jsxs(Styled.Header, {
            className: "ovpn-modal-header",
            "data-empty": title !== undefined,
            children: [title && /*#__PURE__*/_jsx(Text, {
              variant: "textLg",
              weight: "medium",
              color: colors.neutralTextTitle,
              id: "modal_label",
              className: "ovpn-modal-title",
              children: title
            }), !persistent && /*#__PURE__*/_jsx(Styled.CloseButton, {
              "aria-label": "Close",
              tabIndex: -1,
              variant: "tertiary",
              onClick: () => onClose && onClose(false),
              className: "ovpn-modal-close-button",
              children: _X || (_X = /*#__PURE__*/_jsx(X, {
                strokeWidth: 2
              }))
            })]
          }), description && /*#__PURE__*/_jsx(Styled.DescriptionContainer, {
            children: /*#__PURE__*/_jsx(Text, {
              variant: "textSm",
              color: colors.neutralTextBody,
              id: "modal_desc",
              className: "ovpn-modal-description",
              children: description
            })
          }), children && /*#__PURE__*/_jsx(Styled.Content, {
            className: "ovpn-modal-content",
            children: children
          }), footer && /*#__PURE__*/_jsx(Styled.Footer, {
            className: "ovpn-modal-footer",
            children: footer
          })]
        })
      })
    })
  });
});
export default Modal;